import React, { useState, useEffect, useRef, useCallback } from 'react';
import Login from './components/Login';
import { API_BASE_URL } from './config';
import Navigation from './components/Navigation';
import AdminDashboard from './components/AdminDashboard';
import AdminRequests from './components/AdminRequests';
import EmployeeDashboard from './components/EmployeeDashboard';
import EmployeeRequests from './components/EmployeeRequests';
import EmployeeApprovals from './components/EmployeeApprovals';
import AdminReports from './components/AdminReports';
import AdminTriggers from './components/AdminTriggers';
import AdminReplacements from './components/AdminReplacements';
import EmployeeReports from './components/EmployeeReports';
import LookupDatasetsAdmin from './components/LookupDatasetsAdmin';
import './App.css';

const PORTAL_URL = 'https://my.pharmevo.biz/';
const PORTAL_REDIRECT_GUARD = 'portalRedirectAttempted';

export default function App() {
  const [user, setUser] = useState(null);
  const [currentView, setCurrentView] = useState('');
  const [pendingCount, setPendingCount] = useState(0);
  const [authReady, setAuthReady] = useState(false);
  const [redirectingToPortal, setRedirectingToPortal] = useState(false);
  const sessionExpiryHandledRef = useRef(false);

  const clearSession = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('flow');
    setUser(null);
    setCurrentView('');
    setPendingCount(0);
  }, []);

  const handleSessionExpired = useCallback(() => {
    if (sessionExpiryHandledRef.current) return;
    sessionExpiryHandledRef.current = true;
    clearSession();
  }, [clearSession]);

  useEffect(() => {
    const acceptSession = (data, view) => {
      localStorage.setItem('token', data.token);
      localStorage.setItem('user', JSON.stringify(data.user));
      window.history.replaceState({}, '', '/');
      sessionStorage.removeItem(PORTAL_REDIRECT_GUARD);
      sessionExpiryHandledRef.current = false;
      setUser(data.user);
      setCurrentView(view);
    };

    const restoreStoredSession = async () => {
      const token = localStorage.getItem('token');
      const storedUser = localStorage.getItem('user');
      if (!token || !storedUser) return 'none';

      let parsedUser;
      try {
        parsedUser = JSON.parse(storedUser);
      } catch (e) {
        clearSession();
        return 'invalid';
      }

      try {
        const response = await fetch(`${API_BASE_URL}/api/forms`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (response.status === 401) {
          clearSession();
          return 'invalid';
        }

        sessionStorage.removeItem(PORTAL_REDIRECT_GUARD);
        sessionExpiryHandledRef.current = false;
        setUser(parsedUser);
        setCurrentView(parsedUser.role === 'admin' ? 'admin-forms' : 'employee-dashboard');
        return 'ok';
      } catch (e) {
        return 'error';
      }
    };

    const initializeAuth = async () => {
      const params = new URLSearchParams(window.location.search);
      const ssoEmpCode = (params.get('empCode') || '').trim();
      const ssoKey = (params.get('key') || '').trim();
      const approvalToken = (params.get('token') || '').trim();
      const approvalRequestId = (params.get('requestId') || '').trim();
      const forceLogin = params.get('login') === '1';

      if (approvalToken && approvalRequestId) {
        try {
          const response = await fetch(`${API_BASE_URL}/api/auth/approval-link`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ token: approvalToken, requestId: approvalRequestId })
          });
          const data = await response.json();

          if (response.ok) {
            localStorage.setItem('approvalRequestId', approvalRequestId);
            acceptSession(data, data.user.role === 'admin' ? 'admin-requests' : 'employee-approvals');
            setAuthReady(true);
            return;
          }
        } catch (e) {}
      }

      if (ssoEmpCode && ssoKey) {
        try {
          const response = await fetch(
            `${API_BASE_URL}/api/auth/sso?empCode=${encodeURIComponent(ssoEmpCode)}&key=${encodeURIComponent(ssoKey)}`
          );
          const data = await response.json();

          if (response.ok) {
            acceptSession(data, data.user.role === 'admin' ? 'admin-forms' : 'employee-dashboard');
            setAuthReady(true);
            return;
          }
        } catch (e) {}
      }

      const outcome = await restoreStoredSession();

      if (outcome === 'ok' && (ssoKey || ssoEmpCode || approvalToken)) {
        window.history.replaceState({}, '', '/');
      }

      if (forceLogin) sessionStorage.setItem(PORTAL_REDIRECT_GUARD, '1');

      setAuthReady(true);
    };

    initializeAuth();
  }, [clearSession]);

  useEffect(() => {
    if (!authReady || user) return;
    if (sessionStorage.getItem(PORTAL_REDIRECT_GUARD)) return;
    sessionStorage.setItem(PORTAL_REDIRECT_GUARD, '1');
    setRedirectingToPortal(true);
    window.location.replace(PORTAL_URL);
  }, [authReady, user]);

  useEffect(() => {
    const originalFetch = window.fetch.bind(window);

    window.fetch = async (input, init = {}) => {
      const response = await originalFetch(input, init);

      const rawHeaders = init && init.headers ? init.headers : {};
      const authHeader = rawHeaders.Authorization || rawHeaders.authorization;
      const token = localStorage.getItem('token');

      let requestUrl = '';
      if (typeof input === 'string') requestUrl = input;
      else if (input && typeof input.url === 'string') requestUrl = input.url;

      const isApiCall = requestUrl.includes('/api/');
      const shouldTreatAsAuthenticatedRequest = Boolean(token) && (Boolean(authHeader) || isApiCall);

      if (response.status === 401 && shouldTreatAsAuthenticatedRequest) {
        handleSessionExpired();
      }

      return response;
    };

    return () => {
      window.fetch = originalFetch;
    };
  }, [handleSessionExpired]);

  useEffect(() => {
    if (user && user.role === 'employee') {
      
      loadPendingCount();
      if (currentView === 'employee-dashboard') {
        return;
      }
      const interval = setInterval(() => {
        if (currentView !== 'employee-dashboard') loadPendingCount();
      }, 10000);
      return () => clearInterval(interval);
    }
  }, [user, currentView]);

  const loadPendingCount = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/requests/pending-approvals`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.ok) {
        const data = await response.json();
        const pending = data.filter(r => r.status === 'pending' && r.isAwaitingMyApproval).length;
        setPendingCount(pending);
      }
    } catch (error) {
      console.error('Failed to load pending count:', error);
    }
  };

  const handleLogin = (userData) => {
    sessionExpiryHandledRef.current = false;
    sessionStorage.setItem(PORTAL_REDIRECT_GUARD, '1');
    setUser(userData);
    setCurrentView(userData.role === 'admin' ? 'admin-forms' : 'employee-dashboard');
  };

  const handleLogout = () => {
    clearSession();
  };

  const handleNavigate = (view) => {
    setCurrentView(view);
  };

  if (!authReady) {
    return (
      <div style={{ width: '100vw', height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        Checking session...
      </div>
    );
  }

  if (!user) {
    if (redirectingToPortal || !sessionStorage.getItem(PORTAL_REDIRECT_GUARD)) {
      return (
        <div style={{ width: '100vw', height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          Redirecting to PharmEvo portal...
        </div>
      );
    }
    return <Login onLogin={handleLogin} />;
  }

  return (
    <div style={{ width: '100vw', height: '100vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <Navigation
        user={user}
        currentView={currentView}
        onNavigate={handleNavigate}
        onLogout={handleLogout}
        pendingCount={pendingCount}
      />

      <div style={{ flex: 1, overflow: 'auto' }}>
        {user.role === 'admin' && currentView === 'admin-forms' && <AdminDashboard user={user} />}
        {user.role === 'admin' && currentView === 'admin-requests' && <AdminRequests user={user} />}
        {user.role === 'admin' && currentView === 'admin-reports' && <AdminReports user={user} />}
        {user.role === 'admin' && currentView === 'admin-triggers' && <AdminTriggers user={user} />}
        {user.role === 'admin' && currentView === 'admin-replacements' && <AdminReplacements user={user} />}
        {user.role === 'admin' && currentView === 'admin-lookups' && <LookupDatasetsAdmin />}
        {user.role === 'employee' && currentView === 'employee-dashboard' && <EmployeeDashboard user={user} onNavigate={handleNavigate} /> }
        {user.role === 'employee' && currentView === 'employee-requests' && <EmployeeRequests user={user} />}
        {user.role === 'employee' && currentView === 'employee-approvals' && <EmployeeApprovals user={user} />}
        {user.role === 'employee' && currentView === 'employee-reports' && <EmployeeReports user={user} />}
      </div>
    </div>
  );
}
